import { useState, type FormEvent, type ChangeEvent } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Eye, EyeOff, LogIn, X } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { useTranslation } from "react-i18next";

const WAITLIST_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfgfscjIUqfGG4Q49oBBWDdu1o7w7CM-rScwSzwbQGzg2hNkw/viewform";

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation(["auth", "common"]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(formData);
    } catch {
      // Error handling is now done in the useAuth hook with toast notifications
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col">
      <Navbar hideSidebarButtons={true} hideLoginLink={true} />

      <div className="flex-1 px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-center">
          <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-[0_24px_80px_rgba(17,24,39,0.08)]">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />

            <div>
              <section className="p-6 sm:p-8 lg:p-10">
                <div className="mb-10 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-secondary-400">
                      Race Manager
                    </p>
                    <h1 className="mt-3 text-[30px] font-bold tracking-[-0.03em] text-secondary-900 sm:text-[34px]">
                      {t("auth:signIn")}
                    </h1>
                    <p className="mt-3 max-w-md text-sm leading-6 text-secondary-600">
                      {t("auth:signInSubtitle")}
                    </p>
                  </div>

                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-secondary-200 bg-secondary-50 text-primary-600">
                    <LogIn className="h-5 w-5" />
                  </div>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-secondary-700"
                      >
                        {t("auth:email")}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="input mt-2"
                        placeholder={t("auth:email")}
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-secondary-700"
                      >
                        {t("auth:password")}
                      </label>
                      <div className="relative mt-2">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          required
                          className="input pr-11"
                          placeholder={t("auth:password")}
                          value={formData.password}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={
                            showPassword
                              ? t("auth:hidePassword")
                              : t("auth:showPassword")
                          }
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-secondary-400 hover:text-secondary-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 border-t border-secondary-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-secondary-600">
                      {t("auth:dontHaveAccount")}{" "}
                      <button
                        type="button"
                        onClick={() => setShowInvitationModal(true)}
                        className="font-medium text-primary-600 hover:text-primary-500"
                      >
                        {t("auth:register")}
                      </button>
                    </p>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary min-w-[160px] px-5 py-2.5 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                          {t("common:loading")}
                        </div>
                      ) : (
                        t("auth:signIn")
                      )}
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </div>
        </div>
      </div>

      {showInvitationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-secondary-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-secondary-400">
                  {t("auth:privateBeta")}
                </p>
                <h3 className="mt-2 text-xl font-bold tracking-[-0.02em] text-secondary-900">
                  {t("auth:register")}
                </h3>
              </div>

              <button
                onClick={() => setShowInvitationModal(false)}
                className="rounded-md p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mb-6 text-sm leading-6 text-secondary-600">
              {t("auth:invitationOnlyMessage")}
            </p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={WAITLIST_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full px-4 py-2"
              >
                {t("auth:waitlistLink")}
              </a>

              <button
                onClick={() => setShowInvitationModal(false)}
                className="btn-primary w-full px-4 py-2"
              >
                {t("common:ok")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
